/**
 * useVersionCheck Hook
 *
 * Periodically checks for application updates by comparing version information
 * with the server. When a new version is detected, notifies the user.
 *
 * Usage:
 *   const { updateAvailable, newVersion, currentVersion, applyUpdate, dismissUpdate } = useVersionCheck();
 *
 * Features:
 * - Polls meta.json every 5 minutes for version changes
 * - Uses sessionStorage to track dismissals (doesn't nag repeatedly)
 * - Clears caches before reload for clean update
 * - Provides functions to apply or dismiss updates
 * - Stops polling after update is detected
 * - Includes fetch timeout to prevent hanging requests
 */

import { useEffect, useRef, useState } from 'react';
import packageJson from '../../package.json';

const CURRENT_VERSION = packageJson.version;

// ⚠️ TEMPORARY TEST VALUES - CHANGE BACK BEFORE PRODUCTION! ⚠️
// Production: 5 * 60 * 1000 (5 minutes)
// Test: 1 * 60 * 1000 (1 minute) - for faster testing
// const CHECK_INTERVAL = 1 * 60 * 1000; // 1 minute (TESTING ONLY)
const CHECK_INTERVAL = 5 * 60 * 1000; // 1 minute (TESTING ONLY)

const FETCH_TIMEOUT = 10000; // 10 seconds
const DISMISSAL_KEY = 'app-version-update-dismissed';
const RELOAD_ATTEMPT_KEY = 'app-version-reload-attempted';
const RELOAD_ATTEMPT_TIMEOUT = 10 * 60 * 1000; // 10 minutes - prevent reload loops

// ⚠️ TEMPORARY TEST VALUES - CHANGE BACK BEFORE PRODUCTION! ⚠️
// Production: 60 * 60 * 1000 (60 minutes / 1 hour)
// Test: 2 * 60 * 1000 (2 minutes) - for faster testing
// const DISMISSAL_TIMEOUT = 2 * 60 * 1000; // 2 minutes (TESTING ONLY)
const DISMISSAL_TIMEOUT = 60 * 60 * 1000; // 1 hour (TESTING ONLY)

// Conditional logging based on environment
const logger = {
  log: process.env.NODE_ENV === 'development' ? console.log.bind(console) : () => {},
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

export const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [newBuildTime, setNewBuildTime] = useState(null);
  const checkTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const shouldCheckRef = useRef(true); // Track whether to continue checking (prevents stale closure)

  /**
   * Parse and validate dismissed version from sessionStorage
   * Checks if dismissal has expired based on DISMISSAL_TIMEOUT
   * @returns {string|null} The dismissed version or null if expired/invalid
   */
  const getDismissedVersion = () => {
    try {
      const dismissed = sessionStorage.getItem(DISMISSAL_KEY);
      if (!dismissed) return null;

      const parsed = JSON.parse(dismissed);

      // Validate schema
      if (!parsed || typeof parsed.version !== 'string' || !parsed.dismissedAt) {
        logger.warn('[VersionCheck] Invalid dismissal data, removing');
        sessionStorage.removeItem(DISMISSAL_KEY);
        return null;
      }

      // Check if dismissal has expired
      const dismissedTime = new Date(parsed.dismissedAt).getTime();
      const currentTime = Date.now();
      const timeSinceDismissal = currentTime - dismissedTime;

      // Handle invalid timestamps (NaN) or future timestamps (clock changes)
      if (isNaN(dismissedTime) || timeSinceDismissal < 0) {
        logger.warn('[VersionCheck] Invalid or future timestamp detected, clearing dismissal');
        sessionStorage.removeItem(DISMISSAL_KEY);
        return null;
      }

      if (timeSinceDismissal >= DISMISSAL_TIMEOUT) {
        logger.log('[VersionCheck] Dismissal EXPIRED after', Math.round(timeSinceDismissal / 60000), 'min - clearing');
        sessionStorage.removeItem(DISMISSAL_KEY);
        return null;
      }

      logger.log('[VersionCheck] Dismissal ACTIVE -', Math.round((DISMISSAL_TIMEOUT - timeSinceDismissal) / 60000), 'min remaining');
      return parsed.version;
    } catch (error) {
      logger.error('[VersionCheck] Failed to parse dismissal data:', error);
      sessionStorage.removeItem(DISMISSAL_KEY);
      return null;
    }
  };

  /**
   * Store dismissed version in sessionStorage
   * @param {string} version - The version to mark as dismissed
   */
  const setDismissedVersion = (version) => {
    try {
      sessionStorage.setItem(DISMISSAL_KEY, JSON.stringify({
        version,
        dismissedAt: new Date().toISOString(),
      }));
    } catch (error) {
      logger.error('[VersionCheck] Failed to save dismissal:', error);
    }
  };

  /**
   * Validate meta.json response schema
   * @param {any} data - The parsed JSON response
   * @returns {boolean} True if valid, false otherwise
   */
  const isValidMetaResponse = (data) => {
    return (
      data &&
      typeof data === 'object' &&
      typeof data.version === 'string' &&
      data.version.trim().length > 0
    );
  };

  /**
   * Check for new version by fetching meta.json
   * Uses cache-busting and timeout to ensure fresh data
   */
  const checkVersion = async () => {
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const timeout = setTimeout(() => {
      abortControllerRef.current.abort();
    }, FETCH_TIMEOUT);

    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const response = await fetch(`/meta.json?t=${timestamp}`, {
        cache: 'no-store',
        signal: abortControllerRef.current.signal,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      clearTimeout(timeout);

      if (!response.ok) {
        logger.warn('[VersionCheck] Failed to fetch version info:', response.status);
        return;
      }

      const data = await response.json();

      // Validate response schema
      if (!isValidMetaResponse(data)) {
        logger.warn('[VersionCheck] Invalid meta.json response:', data);
        return;
      }

      const serverVersion = data.version;
      const serverBuildTime = data.buildTime; // Optional field

      // Check if user has dismissed this update in current session
      const dismissedVersion = getDismissedVersion();

      logger.log('[VersionCheck] Check:', CURRENT_VERSION, 'vs', serverVersion, '| Dismissed:', dismissedVersion || 'none');

      // Show update notification if:
      // 1. Server version is different from current version
      // 2. User hasn't dismissed this specific version (or dismissal expired)
      if (serverVersion !== CURRENT_VERSION && serverVersion !== dismissedVersion) {
        logger.log('[VersionCheck] SHOW modal - version mismatch, not dismissed');
        setNewVersion(serverVersion);
        setNewBuildTime(serverBuildTime);
        setUpdateAvailable(true);

        // Keep polling to detect when dismissal expires
        // (Don't stop polling anymore - allows re-prompting after timeout)
        logger.log('[VersionCheck] Update shown, continuing checks for dismissal expiration');
      } else if (serverVersion !== CURRENT_VERSION && serverVersion === dismissedVersion) {
        // Update is available but dismissed - need to hide notification if showing
        logger.log('[VersionCheck] HIDE modal - user dismissed this version');
        setUpdateAvailable(false);
      } else {
        logger.log('[VersionCheck] NO ACTION - already on latest version');
        setUpdateAvailable(false);
      }
    } catch (error) {
      clearTimeout(timeout);

      // Don't log abort errors (they're expected when component unmounts)
      if (error.name === 'AbortError') {
        logger.log('[VersionCheck] Request aborted');
      } else {
        // Silently fail to avoid disrupting user experience
        logger.error('[VersionCheck] Error checking version:', error);
      }
    }
  };

  /**
   * Check if we've recently attempted a reload to prevent infinite loops
   * @returns {boolean} True if reload was recently attempted
   */
  const hasRecentReloadAttempt = () => {
    try {
      const attemptData = sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
      if (!attemptData) return false;

      const { timestamp, version } = JSON.parse(attemptData);
      const timeSinceAttempt = Date.now() - timestamp;

      // If we attempted reload for this version within the timeout window, prevent retry
      if (version === newVersion && timeSinceAttempt < RELOAD_ATTEMPT_TIMEOUT) {
        logger.warn('[VersionCheck] Recent reload attempt detected, preventing loop');
        return true;
      }

      // Cleanup old attempt data
      sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
      return false;
    } catch (error) {
      logger.error('[VersionCheck] Error checking reload attempt:', error);
      return false;
    }
  };

  /**
   * Apply the update by clearing caches and reloading the page
   * Includes protection against infinite reload loops
   */
  const applyUpdate = async () => {
    // Check if we've already tried to reload recently
    if (hasRecentReloadAttempt()) {
      logger.error('[VersionCheck] Already attempted reload recently - possible cache issue');

      // Hide the modal to prevent stuck state
      setUpdateAvailable(false);

      // Show user-friendly instructions
      alert(
        'Unable to update automatically. Please:\n' +
        '1. Press Ctrl+Shift+R (Windows/Linux) or Cmd+Shift+R (Mac) to hard refresh\n' +
        '2. Or clear your browser cache manually\n' +
        '3. Then reload the page'
      );
      return;
    }

    logger.log('[VersionCheck] Applying update - clearing caches and reloading');

    // Track this reload attempt to prevent loops
    try {
      sessionStorage.setItem(RELOAD_ATTEMPT_KEY, JSON.stringify({
        timestamp: Date.now(),
        version: newVersion,
      }));
    } catch (error) {
      logger.error('[VersionCheck] Error saving reload attempt:', error);
    }

    // Clear service worker caches if available
    if ('caches' in window) {
      try {
        const names = await caches.keys();
        await Promise.all(names.map(name => {
          logger.log('[VersionCheck] Clearing cache:', name);
          return caches.delete(name);
        }));
      } catch (err) {
        logger.error('[VersionCheck] Error clearing caches:', err);
      }
    }

    // Clear session storage dismissal
    try {
      sessionStorage.removeItem(DISMISSAL_KEY);
    } catch (error) {
      logger.error('[VersionCheck] Error clearing session storage:', error);
    }

    // Force hard refresh by adding cache-busting parameter
    // Note: reload(true) is deprecated, so we use this approach instead
    const url = new URL(window.location.href);
    url.searchParams.set('_refresh', Date.now());
    window.location.href = url.toString();
  };

  /**
   * Dismiss the update notification temporarily (for DISMISSAL_TIMEOUT duration)
   * User will see notification again after timeout expires (60 minutes) if update still available
   * or if they start a new session, or if a different version is detected
   */
  const dismissUpdate = () => {
    logger.log('[VersionCheck] User dismissed update notification for version:', newVersion);

    // Store dismissed version in session storage
    setDismissedVersion(newVersion);

    setUpdateAvailable(false);
  };

  /**
   * Setup periodic version checking
   */
  useEffect(() => {
    logger.log('[VersionCheck] Initializing version check system');
    logger.log('[VersionCheck] Check interval:', CHECK_INTERVAL / 1000, 'seconds');

    // Check immediately on mount
    checkVersion();

    // Set up periodic checking
    const scheduleNextCheck = () => {
      checkTimeoutRef.current = setTimeout(() => {
        checkVersion();
        // Only schedule next check if we should continue (uses ref to avoid stale closure)
        if (shouldCheckRef.current) {
          scheduleNextCheck();
        }
      }, CHECK_INTERVAL);
    };

    scheduleNextCheck();

    // Cleanup on unmount
    return () => {
      shouldCheckRef.current = false; // Stop any pending checks
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
        logger.log('[VersionCheck] Cleanup: cleared check timeout');
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logger.log('[VersionCheck] Cleanup: aborted pending request');
      }
    };
  }, []); // Empty dependency array - only run once on mount

  return {
    updateAvailable,
    newVersion,
    newBuildTime,
    currentVersion: CURRENT_VERSION,
    applyUpdate,
    dismissUpdate,
  };
};
